import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Link,
  Stack,
  Divider,
} from "@material-ui/core";
import { Send, Refresh, Warning, Block, CheckCircle } from "@material-ui/icons";
import { BRAND } from "src/theme/brandTokens";
import { evaluarCompletitudPropuestaV2 } from "src/utils/propuestaV2Completitud";
import { obtenerNombreDestinatario } from "src/utils/propuestasV2/destinatario";
import {
  MENSAJE_DEFAULT_ENVIO,
  MENSAJE_DEFAULT_REENVIO,
} from "src/utils/propuestasV2/mensajesCorreo";

function agruparPorSeccion(errores) {
  const grupos = new Map();
  errores.forEach((err) => {
    const key = err.seccion;
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(err);
  });
  return Array.from(grupos.entries());
}

function ListaErrores({ errores, idPropuesta, onCerrar }) {
  const grupos = agruparPorSeccion(errores);
  return (
    <Stack spacing={2}>
      {grupos.map(([seccion, items]) => (
        <Box key={seccion}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {seccion}
          </Typography>
          <Stack component="ul" sx={{ pl: 3, m: 0 }} spacing={0.5}>
            {items.map((err) => (
              <li key={err.campo}>
                <Link
                  component={RouterLink}
                  to={`/dashboard/propuestas/v2/${idPropuesta}/editar#${err.anchor}`}
                  onClick={onCerrar}
                  underline="hover"
                  sx={{
                    color: BRAND.color.primary,
                    fontFamily: BRAND.font.family.base,
                  }}
                >
                  {err.contexto?.descripcionItem
                    ? `${err.contexto.descripcionItem}: ${err.label}`
                    : err.label}
                </Link>
              </li>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

ListaErrores.propTypes = {
  errores: PropTypes.array.isRequired,
  idPropuesta: PropTypes.number.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

const ModalEnvioPropuestaV2 = ({
  abierto,
  onCerrar,
  propuesta,
  onConfirmarEnvio,
  enviando,
  modoForzado,
  criticalOverride,
}) => {
  const { critical: criticalLocal, recommended } = useMemo(
    () => evaluarCompletitudPropuestaV2(propuesta),
    [propuesta],
  );
  const critical = criticalOverride || criticalLocal;

  let modo;
  if (modoForzado === "reenvio") modo = "reenvio";
  else if (critical.length > 0) modo = "blocked";
  else if (recommended.length > 0) modo = "warning";
  else modo = "ok";

  const [mensaje, setMensaje] = useState(
    modo === "reenvio" ? MENSAJE_DEFAULT_REENVIO : MENSAJE_DEFAULT_ENVIO,
  );

  useEffect(() => {
    setMensaje(
      modo === "reenvio" ? MENSAJE_DEFAULT_REENVIO : MENSAJE_DEFAULT_ENVIO,
    );
  }, [modo, abierto]);

  const nombreDestinatario = obtenerNombreDestinatario(propuesta);
  const correoSolicitante =
    propuesta?.correoSolicitante || propuesta?.solicitante?.correo || "";

  const handleEnviar = () => {
    onConfirmarEnvio(mensaje, modo === "reenvio");
  };

  const tituloPorModo = {
    ok: `Enviar propuesta a ${nombreDestinatario}`,
    warning: "Algunos campos recomendados están vacíos",
    blocked: "No se puede enviar: faltan campos críticos",
    reenvio: `Reenviar propuesta a ${nombreDestinatario}`,
  };

  const iconoPorModo = {
    ok: <CheckCircle sx={{ color: BRAND.color.primary }} />,
    warning: <Warning color="warning" />,
    blocked: <Block color="error" />,
    reenvio: <Refresh sx={{ color: BRAND.color.primary }} />,
  };

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {iconoPorModo[modo]}
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          {tituloPorModo[modo]}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {modo === "blocked" && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Completa los siguientes campos antes de enviar la propuesta al
              cliente:
            </Typography>
            <ListaErrores
              errores={critical}
              idPropuesta={propuesta.id}
              onCerrar={onCerrar}
            />
            {recommended.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Recomendados (no bloquean el envio una vez completados los
                  criticos):
                </Typography>
                <ListaErrores
                  errores={recommended}
                  idPropuesta={propuesta.id}
                  onCerrar={onCerrar}
                />
              </>
            )}
          </>
        )}

        {modo === "warning" && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Faltan algunas secciones recomendadas. Puedes enviar igualmente si
              lo prefieres.
            </Typography>
            <ListaErrores
              errores={recommended}
              idPropuesta={propuesta.id}
              onCerrar={onCerrar}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Se enviará a <strong>{nombreDestinatario}</strong>
              {correoSolicitante ? (
                <>
                  {" "}
                  al correo <strong>{correoSolicitante}</strong>
                </>
              ) : null}
              .
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Mensaje al cliente (editable):
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              variant="outlined"
            />
          </>
        )}

        {modo === "ok" && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              La propuesta está completa. Se enviará a{" "}
              <strong>{nombreDestinatario}</strong>
              {correoSolicitante ? (
                <>
                  {" "}
                  al correo <strong>{correoSolicitante}</strong>
                </>
              ) : null}
              .
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Mensaje al cliente (editable):
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              variant="outlined"
            />
          </>
        )}

        {modo === "reenvio" && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Se reenviará la propuesta a <strong>{nombreDestinatario}</strong>
              {correoSolicitante ? (
                <>
                  {" "}
                  al correo <strong>{correoSolicitante}</strong>
                </>
              ) : null}
              .
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Mensaje al cliente (editable):
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              variant="outlined"
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onCerrar}
          disabled={enviando}
          sx={{ textTransform: "none" }}
        >
          {modo === "blocked" ? "Volver al editor" : "Cancelar"}
        </Button>
        {modo !== "blocked" && (
          <Button
            onClick={handleEnviar}
            disabled={enviando}
            variant="contained"
            startIcon={modo === "reenvio" ? <Refresh /> : <Send />}
            sx={{
              textTransform: "none",
              bgcolor: BRAND.color.primary,
              "&:hover": {
                bgcolor: BRAND.color.primaryDark,
              },
            }}
          >
            {enviando
              ? "Enviando..."
              : modo === "reenvio"
                ? "Reenviar"
                : modo === "warning"
                  ? "Enviar igual"
                  : "Enviar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ModalEnvioPropuestaV2.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  onConfirmarEnvio: PropTypes.func.isRequired,
  enviando: PropTypes.bool,
  modoForzado: PropTypes.oneOf(["reenvio"]),
  criticalOverride: PropTypes.array,
};

ModalEnvioPropuestaV2.defaultProps = {
  enviando: false,
  modoForzado: undefined,
  criticalOverride: null,
};

export default ModalEnvioPropuestaV2;
